import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationDto, NotificationListDto, NotificationSeverity } from '@cpm/shared';
import { Bell, Check, CheckCheck, Settings2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMe } from '../api/auth.js';
import { NotificationEmailCard } from '../components/notification-email.js';
import { NotificationIncident } from '../components/notification-incident.js';
import { api } from '../api/client.js';
import { timeAgo } from '../components/charts.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  PageHeader,
  Spinner,
  Tabs,
} from '../components/ui.js';

/**
 * The staff notification feed (ADR-0014): shared across staff, read state
 * per user. Quota events link to their subscription.
 */

const PER_PAGE = 25;

const severityTone: Record<NotificationSeverity, 'blue' | 'yellow' | 'red'> = {
  info: 'blue',
  warning: 'yellow',
  critical: 'red',
};

function entityLink(notification: NotificationDto): string | null {
  if (!notification.entityId) return null;
  switch (notification.entityType) {
    case 'subscription':
      return `/subscriptions/${notification.entityId}`;
    case 'client':
      return `/clients/${notification.entityId}`;
    default:
      return null;
  }
}

export function NotificationsPage() {
  const me = useMe();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'all' | 'unread'>('unread');
  const [page, setPage] = useState(1);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLElement>(null);

  const list = useQuery({
    queryKey: ['notifications', tab, page],
    queryFn: () =>
      api.get<NotificationListDto>(
        `/api/notifications/?page=${page}&perPage=${PER_PAGE}${tab === 'unread' ? '&unread=true' : ''}`,
      ),
    refetchInterval: 60_000,
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/api/notifications/${id}/read`),
    onSuccess: invalidate,
  });
  const markAllRead = useMutation({
    mutationFn: () => api.post('/api/notifications/read-all'),
    onSuccess: invalidate,
  });

  const totalPages = list.data ? Math.max(1, Math.ceil(list.data.total / PER_PAGE)) : 1;

  return (
    <div>
      <PageHeader
        title="Notifications"
        description="Operational alerts, incident details and other panel events, shared across staff."
        actions={
          <div className="flex flex-wrap gap-2">
            {(list.data?.unreadCount ?? 0) > 0 && (
            <Button
              variant="secondary"
              loading={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              <CheckCheck className="size-4" /> Mark all read
            </Button>
            )}
            {me.data?.role === 'admin' && (
              <Button
                variant="secondary"
                aria-expanded={settingsOpen}
                aria-controls="notification-email-settings"
                onClick={() => setSettingsOpen((open) => !open)}
              >
                <Settings2 className="size-4" aria-hidden /> Email settings
              </Button>
            )}
          </div>
        }
      />

      <Tabs
        tabs={[
          {
            key: 'unread',
            label: `Unread${list.data && list.data.unreadCount > 0 ? ` (${list.data.unreadCount})` : ''}`,
          },
          { key: 'all', label: 'All' },
        ]}
        active={tab}
        onChange={(key) => {
          setTab(key as 'all' | 'unread');
          setPage(1);
        }}
      />

      {list.isPending ? (
        <Spinner />
      ) : list.error ? (
        <ErrorNote error={list.error} />
      ) : list.data.items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Bell className="size-10" />}
            title={tab === 'unread' ? 'All caught up' : 'No notifications yet'}
            description={
              tab === 'unread'
                ? 'Nothing unread — new events will show up here.'
                : 'Panel events like quota crossings will show up here.'
            }
          />
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {list.data.items.map((notification) => {
              const link = entityLink(notification);
              const unread = notification.readAt === null;
              return (
                <li
                  key={notification.id}
                  className={`flex flex-wrap items-start gap-3 px-5 py-4 sm:flex-nowrap ${unread ? '' : 'opacity-70'}`}
                >
                  <span className="mt-0.5">
                    <Badge tone={severityTone[notification.severity]}>
                      {notification.severity}
                    </Badge>
                  </span>
                  <div className="order-3 min-w-0 basis-full sm:order-none sm:flex-1 sm:basis-auto">
                    <p className="text-sm font-medium">
                      {link ? (
                        <Link
                          to={link}
                          className="hover:text-brand-600 hover:underline dark:hover:text-brand-400"
                        >
                          {notification.title}
                        </Link>
                      ) : (
                        notification.title
                      )}
                    </p>
                    {notification.body && (
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-sm text-slate-500 dark:text-slate-400">
                        {notification.body}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
                      <time dateTime={notification.createdAt} title={notification.createdAt}>
                        {new Date(notification.createdAt).toLocaleString()} · {timeAgo(notification.createdAt)}
                      </time>
                    </p>
                    <p className="mt-1 break-all text-xs text-slate-400 dark:text-slate-500">Notification: {notification.id}</p>
                    {notification.entityType === 'monitor_incident' && notification.entityId && (
                      <NotificationIncident incidentId={notification.entityId} />
                    )}
                  </div>
                  {unread && (
                    <button
                      className="ml-auto rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 sm:ml-0 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      title="Mark as read"
                      aria-label={`Mark "${notification.title}" as read`}
                      onClick={() => markRead.mutate(notification.id)}
                    >
                      <Check className="size-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3 text-sm dark:border-slate-800">
              <span className="text-slate-500 dark:text-slate-400">
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
      {me.data?.role === 'admin' && settingsOpen && (
        <section ref={settingsRef} id="notification-email-settings" className="mt-8 scroll-mt-6">
          <NotificationEmailCard onReady={() => settingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
        </section>
      )}
    </div>
  );
}
